import { computed, onMounted, ref, unref } from '@nuxtjs/composition-api';
import { Plans } from './constants';
import { fetchRecurlySellable } from '@/services/PricePlans';

// The ladder sold on the site stops at 300–500; larger companies talk to sales.
const MAX_BAND_SIZE = 500;

/**
 * Plan prices from the Recurly catalog, so a quoted number is one convert will bill. The service
 * caches the request, so every caller shares one fetch.
 *
 * `prices`: formatted monthly price per plan column at `tier` (a number or a ref; band 1 by
 * default), e.g. `{ COMPANY_OBSERVATION: '$199' }`. A key the catalog does not return is absent;
 * callers render no price rather than a stale one.
 *
 * `bands`: the sellable bands, smallest first, as `{ tier, label, value }`. Band 1 is 1–19 in the
 * catalog but reads 0–19, because a company with no staff yet is priced there (BIO-2167).
 */
export default function usePlanPrices(tier = 1) {
  const rows = ref([]);

  onMounted(async () => {
    try {
      const all = await fetchRecurlySellable(
        Plans.tiers.map((t) => t.productKey)
      );
      rows.value = all.filter((r) => !r.annual && r.maxSize <= MAX_BAND_SIZE);
    } catch (err) {
      // A pricing fetch must never take the page with it.
      console.error('Could not load plan prices', err);
    }
  });

  const prices = computed(() =>
    rows.value
      .filter((r) => r.tierNumber === unref(tier))
      .reduce(
        (acc, r) => ({
          ...acc,
          [r.productKey]: `$${r.monthlyPrice.toLocaleString('en-US')}`,
        }),
        {}
      )
  );

  const bands = computed(() =>
    rows.value
      .filter((r) => r.productKey === Plans.tiers[0].productKey)
      .sort((a, b) => a.tierNumber - b.tierNumber)
      .map((r) => ({
        tier: r.tierNumber,
        label: `${r.tierNumber === 1 ? 0 : r.minSize}–${r.maxSize}`,
        // The catalog's own range (`1-19`): what registration stores as the company size, so the
        // plan screen and payment price it exactly (BIO-2201).
        value: r.employeeSizeMapping,
      }))
  );

  return { prices, bands };
}
